import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../api";
import {
  FIELD_CATEGORIES, MEASUREMENT_FIELDS,
  type CurrentSlot, type Measurement, type ReportSubmitBody,
} from "../../types";
import { tg } from "../../telegram";
import { fmtSlot } from "../../utils/date";
import { useT } from "../../i18n";
import MeasurementFieldRow from "../../components/MeasurementFieldRow";

export default function ReportForm() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const turbineId = parseInt(params.get("turbine_id") || "", 10);
  const [slot, setSlot] = useState<CurrentSlot | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Category keys the operator has collapsed. All sections start expanded so
  // the form can be filled top-to-bottom without extra taps.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    setError(null);
    if (!turbineId) {
      setError(t("report_form.no_slot"));
      return;
    }
    // Free-form model: any time the turbine is OK and the user isn't on
    // day-off, has_slot=true and we let them fill the form. The row is
    // created on submit; nothing is "reserved" up-front.
    api.get<CurrentSlot>(`/api/me/current-slot?turbine_id=${turbineId}`)
      .then(s => {
        if (!s.has_slot) {
          setError(s.message ?? t("report_form.no_slot"));
        }
        setSlot(s);
      })
      .catch((e: ApiError) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key: string, raw: string) => {
    setValues(prev => ({ ...prev, [key]: raw }));
  };

  // Fields grouped by category, preserving FIELD_CATEGORIES order.
  const grouped = useMemo(
    () => FIELD_CATEGORIES
      .map(cat => ({ cat, fields: MEASUREMENT_FIELDS.filter(f => f.category === cat.key) }))
      .filter(g => g.fields.length > 0),
    [],
  );

  const filledCount = Object.values(values).filter(v => v.trim() !== "").length;

  const setCatOpen = (key: string, open: boolean) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (open) next.delete(key); else next.add(key);
      return next;
    });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(FIELD_CATEGORIES.map(c => c.key)));

  const submit = async () => {
    if (!turbineId || !slot?.has_slot) return;
    setSubmitting(true);
    setError(null);
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      const body: ReportSubmitBody = {
        turbine_id: turbineId,
        values: { ...values },
      };
      await api.post<Measurement>("/api/me/submit-report", body);
      tg()?.HapticFeedback?.notificationOccurred("success");
      // Go back wherever we came from — works for operators (/report) and
      // admins (/turbines) without role-specific routing here.
      navigate(-1);
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred("error");
      setError((e as ApiError).message);
      setSubmitting(false);
    }
  };

  const cancel = () => {
    tg()?.HapticFeedback?.impactOccurred("light");
    navigate(-1);
  };

  return (
    <div>
      <h1>{t("report_form.title")}</h1>
      {slot?.slot_time && (
        <div className="report-status-line" style={{ marginBottom: 12 }}>
          {t("report_form.slot_label")}: <b>{fmtSlot(slot.slot_time)}</b>
        </div>
      )}

      {error && <div className="alert error">{error}</div>}

      {!slot && !error && (
        <div className="empty-state" style={{ padding: 60 }}>
          <span className="spinner" />
        </div>
      )}

      {slot && slot.has_slot && !slot.is_turbine_running && (
        <div className="alert" style={{
          background: "rgba(255, 149, 0, 0.15)",
          borderLeft: "4px solid #FF9500",
          color: "var(--text)",
          marginBottom: 12,
        }}>
          ⚠ <b>{t("report_form.offline_title")}</b>
          <div style={{ fontSize: 12, marginTop: 4, opacity: 0.9 }}>
            {t("report_form.offline_hint")}
          </div>
        </div>
      )}

      {slot && slot.has_slot && (
        <>
          <div className="report-cats-toolbar">
            <span className="report-cats-count">
              {t("report_form.filled_count", { n: filledCount, total: MEASUREMENT_FIELDS.length })}
            </span>
            <span className="report-cats-actions">
              <button type="button" className="link-btn" onClick={expandAll}>
                {t("report_form.expand_all")}
              </button>
              <button type="button" className="link-btn" onClick={collapseAll}>
                {t("report_form.collapse_all")}
              </button>
            </span>
          </div>

          {grouped.map(({ cat, fields }) => {
            const open = !collapsed.has(cat.key);
            const catFilled = fields.filter(f => (values[f.key] ?? "").trim() !== "").length;
            return (
              <details
                key={cat.key}
                className="cat-section"
                open={open}
                onToggle={e => setCatOpen(cat.key, (e.target as HTMLDetailsElement).open)}
              >
                <summary className="cat-summary">
                  <span className="cat-summary-arrow">▾</span>
                  <span className="cat-summary-label">{cat.label}</span>
                  <span className={"cat-summary-badge" + (catFilled > 0 ? " has" : "")}>
                    {catFilled}/{fields.length}
                  </span>
                </summary>
                <div className="report-form cat-section-body">
                  {fields.map(f => (
                    <MeasurementFieldRow
                      key={f.key}
                      field={f}
                      value={values[f.key] ?? ""}
                      onChange={v => setField(f.key, v)}
                    />
                  ))}
                </div>
              </details>
            );
          })}

          <div className="report-form-actions">
            <button
              className="big-btn primary"
              disabled={submitting}
              onClick={submit}
            >
              <span className="big-btn-icon">📤</span>
              <span className="big-btn-text">
                <b>{submitting ? t("report_form.submitting") : t("report_form.submit")}</b>
                <small>{t("report_form.submit_sub")}</small>
              </span>
            </button>
            <button className="big-btn danger-outline" onClick={cancel} disabled={submitting}>
              <span className="big-btn-icon">✖</span>
              <span className="big-btn-text">
                <b>{t("report_form.cancel")}</b>
                <small>{t("report_form.cancel_sub")}</small>
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
